/**
 * CableLayer
 *
 * Main layer component for rendering cable paths and junction boxes.
 * Handles selection, hover states, interaction logic, and path editing.
 */

import React, { useCallback, useMemo } from 'react';
import { useAppStore, useCablePathVisibility } from '@/stores/appStore';
import { useFloorPlanContext } from '../../FloorPlanProvider';
import { CableJunctionRenderer } from './CableJunctionRenderer';
import { CablePathRenderer } from './CablePathRenderer';
import { JunctionPurposeLabel } from './JunctionPurposeLabel';
import { useCableJunctionsStandardized } from '@/hooks/entities/useCableJunctionsStandardized';
import { useCablePathsStandardized } from '@/hooks/entities/useCablePathsStandardized';
import { getFullPathPoints } from '@/services/cablePathService';
import type { CableJunction, CablePath } from '@/types/cablePaths';
import type { MarkerSizesConfig } from '@/types/floorPlanViewer';
import { logger } from '@/lib/logger';

interface CableLayerProps {
  scale: number;
  onStartDrag?: (
    type: 'cableJunction',
    id: string,
    e: React.MouseEvent | React.TouchEvent,
    elementPos?: { x: number; y: number },
  ) => void;
  isDragging: boolean;
  dragState: {
    type: string;
    id: string;
    startPos: { x: number; y: number };
    currentPos: { x: number; y: number };
    hasMovedThreshold: boolean;
  } | null;
  isPublicView?: boolean;
}

export function CableLayer({
  scale,
  onStartDrag,
  isDragging,
  dragState,
  isPublicView = false,
}: CableLayerProps) {
  // Get floor plan data
  const { floorPlan, scaleData, data } = useFloorPlanContext();
  const floorPlanId = floorPlan.id;

  // Check layer visibility
  const isLayerVisible = useCablePathVisibility();

  // Get junction box base scale from floor plan metadata (like LV products)
  // This makes junction boxes size relative to floor plan dimensions
  const markerSizes = data.floorPlanMetadata?.marker_sizes as MarkerSizesConfig | null;
  const junctionBaseScale = markerSizes?.lv_products_base_scale ?? 1.0;

  // Get cable data using hooks
  const {
    junctions,
    junctionCoordMap,
    isLoading: junctionsLoading,
    updateJunction,
  } = useCableJunctionsStandardized(floorPlanId);
  const { paths, isLoading: pathsLoading } = useCablePathsStandardized(
    floorPlanId,
    junctionCoordMap,
    scaleData,
  );

  // Get store state
  const selectedCablePaths = useAppStore(state => state.selection.cablePaths);
  const selectedCableJunctions = useAppStore(state => state.selection.cableJunctions);
  const hoveredEntityId = useAppStore(state => state.ui.hoveredEntityId);
  const hoveredEntityType = useAppStore(state => state.ui.hoveredEntityType);
  const snapToJunctionId = useAppStore(state => state.cablePaths.snapToJunctionId);
  const activeMode = useAppStore(state => state.ui.toolbar.activeMode);

  // Get store actions
  const selectEntity = useAppStore(state => state.selectEntity);
  const toggleEntitySelection = useAppStore(state => state.toggleEntitySelection);
  const setHoveredEntity = useAppStore(state => state.setHoveredEntity);
  const setActiveDrawer = useAppStore(state => state.setActiveDrawer);

  // Check if interactive and not in drawing mode
  const isDrawingMode = activeMode === 'cable-path-drawing';
  // Allow clicks for selection even during drag (before threshold), but block during active movement
  const isActivelyDragging = isDragging && dragState?.hasMovedThreshold;
  const isInteractive = !isPublicView && !isActivelyDragging && !isDrawingMode;

  // Handle junction click
  const handleJunctionClick = useCallback(
    (e: React.MouseEvent, junction: CableJunction) => {
      if (!isInteractive) return;
      e.stopPropagation();

      logger.log('Junction clicked:', { id: junction.id, name: junction.name });

      if (e.shiftKey || e.metaKey || e.ctrlKey) {
        // Multi-select: use toggleEntitySelection like LV products do
        toggleEntitySelection('cableJunction', junction.id);
        // Check updated selection to determine drawer state
        const state = useAppStore.getState();
        const newSelection = state.selection.cableJunctions;
        if (newSelection.size > 1) {
          setActiveDrawer('selection');
        } else if (newSelection.size === 1) {
          setActiveDrawer('cableJunction');
        } else {
          setActiveDrawer(null);
        }
      } else {
        selectEntity('cableJunction', junction.id);
        setActiveDrawer('cableJunction');
      }
    },
    [isInteractive, selectEntity, toggleEntitySelection, setActiveDrawer],
  );

  // Handle junction mouse down (for dragging)
  const handleJunctionMouseDown = useCallback(
    (e: React.MouseEvent, junction: CableJunction) => {
      if (!isInteractive || !onStartDrag) return;
      onStartDrag('cableJunction', junction.id, e, { x: junction.x, y: junction.y });
    },
    [isInteractive, onStartDrag],
  );

  // Handle junction touch start
  const handleJunctionTouchStart = useCallback(
    (e: React.TouchEvent, junction: CableJunction) => {
      if (!isInteractive || !onStartDrag) return;
      onStartDrag('cableJunction', junction.id, e, { x: junction.x, y: junction.y });
    },
    [isInteractive, onStartDrag],
  );

  // Handle path click
  const handlePathClick = useCallback(
    (e: React.MouseEvent, path: CablePath) => {
      if (!isInteractive) return;
      e.stopPropagation();

      if (e.shiftKey || e.metaKey || e.ctrlKey) {
        // Multi-select: toggle selection like LV products do
        toggleEntitySelection('cablePath', path.id);

        // Check updated selection to determine drawer state
        // Always use 'cablePath' drawer - it handles multi-selection internally
        const newSelection = useAppStore.getState().selection.cablePaths;
        if (newSelection.size > 0) {
          setActiveDrawer('cablePath');
        } else {
          setActiveDrawer(null);
        }
      } else {
        // Single select
        selectEntity('cablePath', path.id);
        setActiveDrawer('cablePath');
      }
    },
    [isInteractive, selectEntity, toggleEntitySelection, setActiveDrawer],
  );

  // Handle path mouse down
  const handlePathMouseDown = useCallback(
    (e: React.MouseEvent, path: CablePath) => {
      if (!isInteractive) return;
      // Paths don't drag, but we still capture the event
      e.stopPropagation();
    },
    [isInteractive],
  );

  // Handle hover enter
  const handleHoverEnter = useCallback(
    (type: 'cablePath' | 'cableJunction', id: string) => {
      if (!isInteractive) return;
      setHoveredEntity(id, type);
    },
    [isInteractive, setHoveredEntity],
  );

  // Handle hover leave
  const handleHoverLeave = useCallback(() => {
    setHoveredEntity(null, null);
  }, [setHoveredEntity]);

  // Check if we're dragging a junction via the unified drag handler
  const isDraggingJunction =
    isDragging && dragState?.type === 'cableJunction' && dragState?.hasMovedThreshold;
  const draggedJunctionId = isDraggingJunction ? dragState?.id : null;
  const draggedJunctionPos = isDraggingJunction ? dragState?.currentPos : null;

  // Create a modified junction coord map that includes drag preview position
  const effectiveJunctionCoordMap = useMemo(() => {
    if (!isDraggingJunction || !draggedJunctionId || !draggedJunctionPos) {
      return junctionCoordMap;
    }

    // Clone the map and update the dragged junction position
    const newMap = new Map(junctionCoordMap);
    const existingJunction = newMap.get(draggedJunctionId);
    if (existingJunction) {
      newMap.set(draggedJunctionId, {
        ...existingJunction,
        x: draggedJunctionPos.x,
        y: draggedJunctionPos.y,
      });
    }
    return newMap;
  }, [junctionCoordMap, isDraggingJunction, draggedJunctionId, draggedJunctionPos]);

  // Compute full points for each path (including junction coordinates)
  // Note: Path edit handle drag preview is handled in CablePathEditOverlay
  const pathsWithFullPoints = useMemo(() => {
    return paths.map(path => {
      const fullPoints = getFullPathPoints(path, effectiveJunctionCoordMap);
      return { path, fullPoints };
    });
  }, [paths, effectiveJunctionCoordMap]);

  // Don't render if loading or layer is hidden
  if (junctionsLoading || pathsLoading || !isLayerVisible) {
    return null;
  }

  return (
    <g className="cable-layer" data-cable-layer="true">
      {/* Render paths first (below junctions) */}
      {pathsWithFullPoints.map(({ path, fullPoints }) => (
        <CablePathRenderer
          key={path.id}
          path={path}
          fullPoints={fullPoints}
          isSelected={selectedCablePaths.has(path.id)}
          isHovered={hoveredEntityId === path.id && hoveredEntityType === 'cablePath'}
          scale={scale}
          scaleData={scaleData}
          onClick={e => handlePathClick(e, path)}
          onMouseEnter={() => handleHoverEnter('cablePath', path.id)}
          onMouseLeave={handleHoverLeave}
          onMouseDown={e => handlePathMouseDown(e, path)}
          isInteractive={isInteractive}
        />
      ))}

      {/* Render junctions */}
      {junctions.map(junction => {
        // Use dragged position if this junction is being dragged
        const displayJunction =
          isDraggingJunction && draggedJunctionId === junction.id && draggedJunctionPos
            ? { ...junction, x: draggedJunctionPos.x, y: draggedJunctionPos.y }
            : junction;

        return (
          <CableJunctionRenderer
            key={junction.id}
            junction={displayJunction}
            isSelected={selectedCableJunctions.has(junction.id)}
            isHovered={hoveredEntityId === junction.id && hoveredEntityType === 'cableJunction'}
            isSnapTarget={snapToJunctionId === junction.id}
            scale={scale}
            baseScale={junctionBaseScale}
            onClick={e => handleJunctionClick(e, junction)}
            onMouseEnter={() => handleHoverEnter('cableJunction', junction.id)}
            onMouseLeave={handleHoverLeave}
            onMouseDown={e => handleJunctionMouseDown(e, junction)}
            onTouchStart={e => handleJunctionTouchStart(e, junction)}
            isInteractive={isInteractive}
            isDragging={isDraggingJunction && draggedJunctionId === junction.id}
          />
        );
      })}

      {/* Render junction purpose labels */}
      {junctions.map(junction => {
        if (!junction.purpose_type || !junction.purpose_label) return null;

        // Use dragged position if this junction is being dragged
        const displayJunction =
          isDraggingJunction && draggedJunctionId === junction.id && draggedJunctionPos
            ? { ...junction, x: draggedJunctionPos.x, y: draggedJunctionPos.y }
            : junction;

        return (
          <JunctionPurposeLabel
            key={`purpose-${junction.id}`}
            junction={displayJunction}
            scale={scale}
            isSelected={selectedCableJunctions.has(junction.id)}
            isInteractive={isInteractive}
            onUpdateLabel={updates => updateJunction(junction.id, updates)}
          />
        );
      })}

      {/* Edit handles are now rendered in CablePathEditOverlay for better z-index handling */}
    </g>
  );
}
